// Google Analytics 4 behind visitor consent, for an instance that sets
// TRES_API_GA4_MEASUREMENT_ID. Until the visitor accepts, nothing Google-related
// reaches the page: no script, no dataLayer, no cookies.

const CONSENT_STORAGE_KEY = 'tres.analytics_consent';
const CONSENT_MAX_AGE_MS = 365 * 24 * 60 * 60 * 1000;
const GTAG_SRC = 'https://www.googletagmanager.com/gtag/js';

export type ConsentDecision = 'granted' | 'denied';

interface StoredConsent {
  decision: ConsentDecision;
  decided_at: string;
}

type Gtag = (...args: unknown[]) => void;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: Gtag;
  }
}

/** The visitor's analytics decision, or null when there is none, it cannot be
 *  read, or it is more than a year old (a stale decision is asked again). */
export function readConsent(now: number = Date.now()): ConsentDecision | null {
  try {
    const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const stored = JSON.parse(raw) as Partial<StoredConsent>;
    if (stored.decision !== 'granted' && stored.decision !== 'denied') return null;
    const decidedAt = Date.parse(stored.decided_at ?? '');
    if (!Number.isFinite(decidedAt) || now - decidedAt > CONSENT_MAX_AGE_MS) return null;
    return stored.decision;
  } catch {
    return null;
  }
}

export function writeConsent(decision: ConsentDecision, now: number = Date.now()): void {
  const stored: StoredConsent = { decision, decided_at: new Date(now).toISOString() };
  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(stored));
  } catch {
    /* localStorage unavailable: the choice holds for this page session and is asked again next visit. */
  }
}

// Addresses carry entity URIs and search text in the query and hash, and GA
// forbids personal data, so a page is reported by its path alone.
function pathOnlyLocation(pathname: string): string {
  return `${window.location.origin}${pathname}`;
}

/** Start GA4 after the visitor granted consent. The tag is injected once per page. */
export function loadAnalytics(measurementId: string): void {
  if (document.querySelector(`script[src^="${GTAG_SRC}"]`)) return;

  const dataLayer = (window.dataLayer ??= []);
  const gtag: Gtag = function () {
    // gtag.js acts only on Arguments objects; an array from rest parameters is ignored.
    // eslint-disable-next-line prefer-rest-params
    dataLayer.push(arguments);
  };
  window.gtag = gtag;

  gtag('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'granted',
  });
  gtag('js', new Date());
  gtag('config', measurementId, {
    send_page_view: false,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
    page_location: pathOnlyLocation(window.location.pathname),
  });
  const script = document.createElement('script');
  script.async = true;
  script.src = `${GTAG_SRC}?id=${encodeURIComponent(measurementId)}`;
  document.head.appendChild(script);
}

export function sendPageView(pathname: string): void {
  window.gtag?.('event', 'page_view', {
    page_location: pathOnlyLocation(pathname),
    page_path: pathname,
    page_title: document.title,
  });
}
